import type { StationTelemetrySeriesDto } from '@/features/admin/types/stationTelemetry.types';

import { unwrapOpsList, unwrapOpsObject } from './opsResponse';

function num(v: unknown): number | undefined {
  if (typeof v === 'number' && !Number.isNaN(v)) return v;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    if (!Number.isNaN(n)) return n;
  }
  return undefined;
}

function timeToMs(v: unknown): number | undefined {
  if (typeof v === 'number' && !Number.isNaN(v)) {
    return v < 1e12 ? v * 1000 : v;
  }
  if (typeof v === 'string' && v) {
    const d = Date.parse(v);
    if (!Number.isNaN(d)) return d;
  }
  return undefined;
}

/**
 * Normalizes assorted API shapes into `{ t: ms, v: number }[]` sorted by `t`.
 */
export function normalizeStationTelemetryPoints(raw: unknown): Array<{ t: number; v: number }> {
  const out: Array<{ t: number; v: number }> = [];

  const pushRow = (row: Record<string, unknown>) => {
    const t =
      timeToMs(row.t) ??
      timeToMs(row.ts) ??
      timeToMs(row.time) ??
      timeToMs(row.timestamp) ??
      timeToMs(row.observedAt) ??
      timeToMs(row.observed_at);
    const v =
      num(row.v) ??
      num(row.value) ??
      num(row.reading) ??
      num(row.level_m) ??
      num(row.value_m) ??
      num(row.level);
    if (t !== undefined && v !== undefined) out.push({ t, v });
  };

  const enveloped = unwrapOpsObject<Record<string, unknown>>(raw);
  const base =
    enveloped ??
    (raw && typeof raw === 'object' && !Array.isArray(raw)
      ? (raw as Record<string, unknown>)
      : null);

  let list: unknown[];
  if (base && Array.isArray(base.points)) {
    list = base.points as unknown[];
  } else if (Array.isArray(raw)) {
    list = raw as unknown[];
  } else {
    list = unwrapOpsList<Record<string, unknown>>(raw);
  }

  for (const item of list) {
    if (item && typeof item === 'object' && !Array.isArray(item)) {
      pushRow(item as Record<string, unknown>);
    }
  }

  out.sort((a, b) => a.t - b.t);
  return out;
}

export function toRechartsHistory(points: Array<{ t: number; v: number }>): {
  date: string;
  value: number;
}[] {
  return points.map((p) => ({
    date: new Date(p.t).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }),
    value: p.v,
  }));
}

export function emptySeries(
  source: StationTelemetrySeriesDto['source'],
): StationTelemetrySeriesDto {
  return { points: [], source };
}
